import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useNavigate, useParams } from 'react-router-dom';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Grid, Box, Tab, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Theme } from '@mui/material/styles';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import MaterialSettings from 'components/lab-settings/material-settings/material-settings.componenet';
import ActionSettings from 'components/lab-settings/action-settings/action-settings.component';
import DeviationSettings from 'components/lab-settings/deviation-settings/deviation-settings.components';
import NotificationSettings from 'components/lab-settings/notification-settings/notification-settings.components';
import GeneralSettings from 'components/lab-settings/general-settings/general-settings.components';
import AvailabilitySettings from 'components/lab-settings/availability-settings/availability-settings.components';
import TransportServiceSettings from 'components/lab-settings/transport-service-settings/transport-service-settings.components';
import InvoiceSettings from 'components/lab-settings/invoice-settings/invoice-settings.components';
import MillingJobSettings from 'components/lab-settings/milling-job-settings/milling-job-settings.components';
import OrderTagsSettings from 'components/lab-settings/order-tags/order-tags-setting.component';
import ProductionWorkflowSettings from 'components/lab-settings/production-workflow-settings/production-workflow-settings.components';
import ProductionValueSettings from 'components/lab-settings/production-value-settings/production-value-settings.components';
import LabApiClientSettings from 'components/lab-settings/api-client-settings/api-client-settings.component';
import PanNumberSettings from 'components/lab-settings/pan-number-settings/pan-number-settings.component';
import ListViewsSettings from 'components/lab-settings/list-views/list-views.component';
import DocumentBundlesSettings from 'components/lab-settings/document-bundles/document-bundles.component';
import UsageOverview from 'components/usage/usage-overview.component';

const LabSettings: FC = () => {
    const { tab } = useParams<{ tab: string }>();
    const [tabState, setTabState] = useState<string>('general-information');
    const { t: translate } = useTranslation(['lab-settings', 'deviations', 'milling-jobs']);
    const navigate = useNavigate();
    const currentOrganization = currentOrgVar();
    // DD-3671: usage figures are for lab admins only; the backend refuses everyone else.
    const canSeeUsage = permissions.isTechnicianAdmin(currentUserVar().role);

    const isLabWorkflowEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_WORKFLOW,
    );
    const isLabMillingEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_MILLING,
    );

    useEffect(() => {
        // DD-3689: Profile & media is now part of General information; old links land there.
        if (tab === 'profile-and-media') {
            navigate('/lab-settings/general-information', { replace: true });
            return;
        }
        setTabState(tab && (tab !== 'usage' || canSeeUsage) ? tab : 'general-information');
    }, [tab, canSeeUsage]);

    const handleTabChange = (_event: unknown, newTabState: string) => {
        navigate(`/lab-settings/${newTabState}`);
        setTabState(newTabState);
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('lab-settings:SETTINGS')} />
            <TabContext value={tabState}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={2}>
                        <Paper variant="outlined" square>
                            <Box>
                                <TabList orientation="vertical" variant="scrollable" onChange={handleTabChange}>
                                    <SettingsTab
                                        label={translate('lab-settings:GENERAL_INFO')}
                                        value="general-information"
                                    />
                                    <SettingsTab
                                        label={translate('lab-settings:NOTIFICATIONS')}
                                        value="notification-settings"
                                    />
                                    {isLabWorkflowEnabled ? (
                                        <SettingsTab
                                            label={translate('lab-settings:PRODUCTION_WORKFLOW')}
                                            value="production-workflow"
                                        />
                                    ) : undefined}

                                    <SettingsTab
                                        label={translate('lab-settings:PRODUCTION_VALUE')}
                                        value="production-value"
                                    />

                                    <SettingsTab
                                        label={translate('lab-settings:MATERIALS')}
                                        value="material-settings"
                                    />
                                    <SettingsTab label={translate('lab-settings:ACTIONS')} value="action-settings" />
                                    <SettingsTab
                                        label={translate('lab-settings:OPENING_HOURS_AND_AVAILABILITY')}
                                        value="availability"
                                    />
                                    <SettingsTab
                                        label={translate('lab-settings:TRANSPORT_SERVICES')}
                                        value="transport-service"
                                    />
                                    <SettingsTab label={translate('lab-settings:INVOICING')} value="invoicing" />
                                    <SettingsTab
                                        label={translate('deviations:DEVIATIONS')}
                                        value="deviation-settings"
                                    />
                                    {isLabMillingEnabled ? (
                                        <SettingsTab
                                            label={translate('milling-jobs:MILLING_JOBS')}
                                            value="milling-job-settings"
                                        />
                                    ) : undefined}
                                    <SettingsTab label={translate('lab-settings:ORDER_TAGS')} value="order-tags" />
                                    <SettingsTab label={translate('lab-settings:PAN_NUMBERS')} value="pan-numbers" />
                                    <SettingsTab label={translate('lab-settings:LIST_VIEWS')} value="list-views" />
                                    <SettingsTab
                                        label={translate('lab-settings:DOCUMENT_BUNDLES')}
                                        value="document-bundles"
                                    />
                                    <SettingsTab
                                        label={translate('lab-settings:API_AND_INTEGRATIONS')}
                                        value="api-and-integrations"
                                    />
                                    {canSeeUsage ? (
                                        <SettingsTab label={translate('lab-settings:USAGE')} value="usage" />
                                    ) : undefined}
                                </TabList>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={10}>
                        <TabPanel sx={{ padding: 0 }} value="general-information">
                            <Paper>
                                <Box p={2}>
                                    <GeneralSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="notification-settings">
                            <Paper>
                                <Box p={2}>
                                    <NotificationSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        {isLabWorkflowEnabled ? (
                            <TabPanel sx={{ padding: 0 }} value="production-workflow">
                                <Paper>
                                    <Box p={2}>
                                        <ProductionWorkflowSettings />
                                    </Box>
                                </Paper>
                            </TabPanel>
                        ) : undefined}

                        <TabPanel sx={{ padding: 0 }} value="production-value">
                            <Paper>
                                <Box p={2}>
                                    <ProductionValueSettings />
                                </Box>
                            </Paper>
                        </TabPanel>

                        <TabPanel sx={{ padding: 0 }} value="material-settings">
                            <Paper>
                                <Box p={2}>
                                    <MaterialSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="action-settings">
                            <Paper>
                                <Box p={2}>
                                    <ActionSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="availability">
                            <Paper>
                                <Box p={2}>
                                    <AvailabilitySettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="transport-service">
                            <Paper>
                                <Box p={2}>
                                    <TransportServiceSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="invoicing">
                            <Paper>
                                <Box p={2}>
                                    <InvoiceSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="deviation-settings">
                            <Paper>
                                <Box p={2}>
                                    <DeviationSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        {isLabMillingEnabled ? (
                            <TabPanel sx={{ padding: 0 }} value="milling-job-settings">
                                <Paper>
                                    <Box p={2}>
                                        <MillingJobSettings />
                                    </Box>
                                </Paper>
                            </TabPanel>
                        ) : undefined}
                        <TabPanel sx={{ padding: 0 }} value="order-tags">
                            <Paper>
                                <Box p={2}>
                                    <OrderTagsSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="pan-numbers">
                            <Paper>
                                <Box p={2}>
                                    <PanNumberSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="list-views">
                            <Paper>
                                <Box p={2}>
                                    <ListViewsSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="document-bundles">
                            <Paper>
                                <Box p={2}>
                                    <DocumentBundlesSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="api-and-integrations">
                            <Paper>
                                <Box p={2}>
                                    <LabApiClientSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        {canSeeUsage ? (
                            <TabPanel sx={{ padding: 0 }} value="usage">
                                <Paper>
                                    <Box p={2}>
                                        <UsageOverview />
                                    </Box>
                                </Paper>
                            </TabPanel>
                        ) : undefined}
                    </Grid>
                </Grid>
            </TabContext>
        </Fragment>
    );
};

interface SettingsTabProps {
    label: string;
    value: string;
    theme?: Theme;
}
const SettingsTab = styled((props: SettingsTabProps) => <Tab {...props} />)(({ theme }: { theme?: Theme }) => {
    return {
        maxWidth: 'none',
        fontSize: '0.7em',
        '&.Mui-selected': {
            fontWeight: theme?.typography.fontWeightBold,
            background: theme?.palette.primary.main,
            color: '#fff',
        },
        '&:hover': {
            opacity: 0.8,
        },
        alignItems: 'start',
        textAlign: 'left',
    };
});
export default LabSettings;
